import React from 'react';

import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { UserType } from 'src/accounts/types';
import { Toast } from 'src/common/components';
import { getUserInitial } from 'src/common/utils';

import { EMAIL_REGEX } from '../../constants';
import { updateUser } from '../api';
import { Action } from '../constants';
import { useAccountProfileContext } from '../context';

type UsersItemProps = {
  user: UserType;
  setIsEditing: (isEditing: boolean) => void;
};

type FormType = {
  available_accounts_for_switch: Array<{ id: string; name: string }>;
  email: string;
  password?: string;
  confirm_password?: string;
  first_name: string;
  last_name: string;
  is_account_manager: boolean;
  can_switch_accounts: boolean;
};

const UsersItemEditing: React.FC<UsersItemProps> = ({ user, setIsEditing }: UsersItemProps) => {
  // TODO: THIS WILL BE NEEDED WHEN CREATING USERS;
  const isCreatingUser = false;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormType>({
    defaultValues: { ...user, password: '', confirm_password: '' },
  });
  const { dispatch } = useAccountProfileContext();
  const userName = `${user.first_name} ${user.last_name}`;

  const handleSetIsEditing = (): void => {
    setIsEditing(false);
  };

  const validatePassword = (value: string | undefined): boolean => {
    const { password } = getValues();
    if (!isCreatingUser && !password) return true;
    if (password !== value) return false;
    return true;
  };

  const handleUpdateUser = async (data: FormType): Promise<void> => {
    const newUserInformation = { ...data };
    if (!isCreatingUser && !data.password) {
      delete newUserInformation.password;
      delete newUserInformation.confirm_password;
    }
    try {
      await updateUser({
        id: user.id,
        ...newUserInformation,
        available_accounts_for_switch:
          newUserInformation.available_accounts_for_switch?.map((acc) => acc?.id) ?? [],
      });
      dispatch({
        type: Action.UPDATE_USER,
        userId: user.id,
        data: newUserInformation,
      });
      handleSetIsEditing();
      Toast.success('User was saved successfully.');
    } catch (err) {
      const error = err as AxiosError;
      let errorMessage = 'Unknown error. Could not save user information for the moment.';
      if (error.response) {
        const { status } = error.response;
        if (status === 404) {
          errorMessage = 'User could not be found.';
        }
      }
      Toast.error(errorMessage);
    }
  };

  return (
    <form
      className="flex flex-col flex-grow bg-white-1.0 p-4 rounded"
      onSubmit={handleSubmit(handleUpdateUser)}
    >
      <div className="flex mt-4">
        <span className="my-auto items-center justify-center w-10 h-10 bg-primary-1.0 rounded-full flex">
          <p className="font-medium text-white-0.0">{getUserInitial(user)}</p>
        </span>
        <div className="flex flex-col my-auto ml-2">
          <h3>{userName}</h3>
          <div className="flex">
            <p className="text-gray-1.0 font-semibold my-auto ml-2">Usuario:</p>
            <p className="text-gray-1.0 font-semibold my-auto ml-2">{user.username}</p>
          </div>
        </div>
        <button type="button" className="ml-auto" onClick={handleSetIsEditing}>
          <i className="icon-reject_icon text-xl text-gray-1.0 font-semibold" />
        </button>
      </div>
      <div className="md:grid-cols-5 gap-y-6 grid grid-cols-1 mt-6">
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="first_name" className="input-label">
            First name
            <input
              id="first_name"
              className="input w-full mt-1 transition duration-150 ease-in-out"
              {...register('first_name', { required: true })}
              defaultValue=""
              data-testid="first-name"
              autoComplete="off"
            />
            {errors.first_name && <p className="error-text"> The first name is required</p>}
          </label>
        </div>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="last_name" className="input-label">
            Last name
            <input
              id="last_name"
              className="input w-full mt-1 transition duration-150 ease-in-out"
              {...register('last_name', { required: true })}
              defaultValue=""
              data-testid="last-name"
              autoComplete="off"
            />
            {errors.last_name && <p className="error-text"> The last name is required</p>}
          </label>
        </div>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="email" className="input-label">
            Email
            <input
              id="email"
              className="input w-full mt-1 transition duration-150 ease-in-out"
              {...register('email', {
                required: 'The email is required',
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Entered value does not match email format',
                },
              })}
              defaultValue=""
              data-testid="email"
              autoComplete="off"
              type="text"
            />
            {errors.email && <p className="error-text"> {errors.email?.message}</p>}
          </label>
        </div>
        <h3 className="col-start-1 col-end-7 mt-12">Cambio de contraseña</h3>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="password" className="input-label">
            Password
            <input
              id="password"
              className="input w-full mt-1 transition duration-150 ease-in-out"
              {...register('password', { required: isCreatingUser })}
              defaultValue=""
              data-testid="password"
              autoComplete="off"
              type="password"
            />
            {errors.password && <p className="error-text"> The password is required</p>}
          </label>
        </div>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="confirm_password" className="input-label">
            Confirm password
            <input
              id="confirm_password"
              className="input w-full mt-1 transition duration-150 ease-in-out"
              {...register('confirm_password', {
                required: isCreatingUser,
                validate: validatePassword,
              })}
              defaultValue=""
              data-testid="confirm-password"
              autoComplete="off"
              type="password"
            />
            {errors.confirm_password && (
              <p className="error-text"> The confirm password is required</p>
            )}
          </label>
        </div>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="is_account_manager" className=" mt-4">
            <div className="flex">
              <p className="input-label">Is account manager?</p>
              <input
                id="is_account_manager"
                className="my-auto ml-4 transition duration-150 ease-in-out"
                {...register('is_account_manager')}
                data-testid="is-account-manager"
                type="checkbox"
              />
            </div>
            <p className="helper-text">True if the user is an admin for this account.</p>
          </label>
        </div>
        <div className="sm:col-end-3 col-start-1 col-end-1">
          <label htmlFor="is_account_manager" className=" mt-4">
            <div className="flex">
              <p className="input-label">Can switch accounts?</p>
              <input
                id="can_switch_accounts"
                className="my-auto ml-4 transition duration-150 ease-in-out"
                {...register('can_switch_accounts')}
                data-testid="can-switch-accounts"
                type="checkbox"
              />
            </div>
            <p className="helper-text">True if the user is allowed to change between accounts.</p>
          </label>
        </div>
      </div>
      <div className="flex justify-end flex-shrink-0 mt-6">
        <button type="button" className="btn btn-ghost" onClick={handleSetIsEditing}>
          Cancel
        </button>
        <button type="submit" className="btn btn-secondary ml-3" data-testid="user-submit">
          Save
        </button>
      </div>
    </form>
  );
};

export default UsersItemEditing;
